import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TourPackageItem, TourPackageFormData, TourItem } from "@/types/database";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Trash, Search, ChevronsUpDown, Check } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ItineraryForm } from "@/components/tour-package/itinerary-form";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export default function TourPackageForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState<TourPackageFormData>({
    tours_id: "",
    selected_tour: [],
    tour_package_name: "",
    // MPV Transport
    transport_kuta: 0,
    transport_nusadua: 0,
    transport_ubud: 0,
    transport_other: 0,
    // Elf Short Transport
    elf_short_transport_kuta: 0,
    elf_short_transport_nusadua: 0,
    elf_short_transport_ubud: 0,
    elf_short_transport_other: 0,
    // Hiace Transport
    hiace_transport_kuta: 0,
    hiace_transport_nusadua: 0,
    hiace_transport_ubud: 0,
    hiace_transport_other: 0,
    // Elf Giga Transport
    elf_giga_transport_kuta: 0,
    elf_giga_transport_nusadua: 0,
    elf_giga_transport_ubud: 0,
    elf_giga_transport_other: 0,
    // Bus Transport
    bus_transport_kuta: 0,
    bus_transport_nusadua: 0,
    bus_transport_ubud: 0,
    bus_transport_other: 0,
    sub_total_price_child: 0,
    sub_total_price_adult: 0,
    sub_total_parking_price: {
      mpv: 0,
      elf_short: 0,
      hiace: 0,
      elf_giga: 0,
      bus: 0
    },
    itinerary: "",
  });

  // Fetch tours for selection
  const { data: tours = [] } = useQuery({
    queryKey: ["tours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        toast.error(error.message);
        throw error;
      }
      return data as TourItem[];
    },
  });

  // Fetch tour package if editing
  const { data: tourPackage } = useQuery({
    queryKey: ["tour_package", id],
    queryFn: async () => {
      if (!id) return null;

      const { data, error } = await supabase
        .from("tour_package")
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        toast.error(error.message);
        throw error;
      }
      return data as TourPackageItem;
    },
    enabled: Boolean(id),
  });

  // Load tour package data when editing
  useEffect(() => {
    if (tourPackage) {
      setFormData({
        tour_package_name: tourPackage.tour_package_name,
        tours_id: tourPackage.tours_id,
        selected_tour: tourPackage.selected_tour,
        // MPV Transport
        transport_kuta: tourPackage.transport_kuta || 0,
        transport_nusadua: tourPackage.transport_nusadua || 0,
        transport_ubud: tourPackage.transport_ubud || 0,
        transport_other: tourPackage.transport_other || 0,
        // Elf Short Transport
        elf_short_transport_kuta: tourPackage.elf_short_transport_kuta || 0,
        elf_short_transport_nusadua: tourPackage.elf_short_transport_nusadua || 0,
        elf_short_transport_ubud: tourPackage.elf_short_transport_ubud || 0,
        elf_short_transport_other: tourPackage.elf_short_transport_other || 0,
        // Hiace Transport
        hiace_transport_kuta: tourPackage.hiace_transport_kuta || 0,
        hiace_transport_nusadua: tourPackage.hiace_transport_nusadua || 0,
        hiace_transport_ubud: tourPackage.hiace_transport_ubud || 0,
        hiace_transport_other: tourPackage.hiace_transport_other || 0,
        // Elf Giga Transport
        elf_giga_transport_kuta: tourPackage.elf_giga_transport_kuta || 0,
        elf_giga_transport_nusadua: tourPackage.elf_giga_transport_nusadua || 0,
        elf_giga_transport_ubud: tourPackage.elf_giga_transport_ubud || 0,
        elf_giga_transport_other: tourPackage.elf_giga_transport_other || 0,
        // Bus Transport
        bus_transport_kuta: tourPackage.bus_transport_kuta || 0,
        bus_transport_nusadua: tourPackage.bus_transport_nusadua || 0,
        bus_transport_ubud: tourPackage.bus_transport_ubud || 0,
        bus_transport_other: tourPackage.bus_transport_other || 0,
        sub_total_price_child: tourPackage.sub_total_price_child,
        sub_total_price_adult: tourPackage.sub_total_price_adult,
        sub_total_parking_price: {
          mpv: tourPackage.sub_total_parking_price_mpv || 0,
          elf_short: tourPackage.sub_total_parking_price_elf_short || 0,
          hiace: tourPackage.sub_total_parking_price_hiace || 0,
          elf_giga: tourPackage.sub_total_parking_price_elf_giga || 0,
          bus: tourPackage.sub_total_parking_price_bus || 0,
        },
        itinerary: tourPackage.itinerary || "",
      });
    }
  }, [tourPackage]);

  // Add tour package mutation
  const addTourPackageMutation = useMutation({
    mutationFn: async (newPackage: TourPackageFormData) => {
      // Extract parking prices to individual fields
      const {
        sub_total_parking_price,
        ...restPackage
      } = newPackage;

      const { data, error } = await supabase
        .from("tour_package")
        .insert([{
          ...restPackage,
          tour_package_name: Array.isArray(newPackage.selected_tour) 
            ? newPackage.selected_tour.map(tour => tour.tour_name).join(" + ")
            : "",
          sub_total_parking_price_mpv: sub_total_parking_price.mpv,
          sub_total_parking_price_elf_short: sub_total_parking_price.elf_short,
          sub_total_parking_price_hiace: sub_total_parking_price.hiace,
          sub_total_parking_price_elf_giga: sub_total_parking_price.elf_giga,
          sub_total_parking_price_bus: sub_total_parking_price.bus,
        }])
        .select();

      if (error) {
        toast.error(error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tour_packages"] });
      toast.success("Tour package added successfully!");
      navigate("/tourpackage");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  // Edit tour package mutation
  const editTourPackageMutation = useMutation({
    mutationFn: async (updatedPackage: TourPackageFormData) => {
      if (!id) {
        throw new Error("Invalid tour package ID");
      }

      // Extract parking prices to individual fields
      const {
        sub_total_parking_price,
        ...restPackage
      } = updatedPackage;

      const { error } = await supabase
        .from("tour_package")
        .update({
          ...restPackage,
          sub_total_parking_price_mpv: sub_total_parking_price.mpv,
          sub_total_parking_price_elf_short: sub_total_parking_price.elf_short,
          sub_total_parking_price_hiace: sub_total_parking_price.hiace,
          sub_total_parking_price_elf_giga: sub_total_parking_price.elf_giga,
          sub_total_parking_price_bus: sub_total_parking_price.bus,
        })
        .eq("id", id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tour_packages"] });
      toast.success("Tour package updated successfully!");
      navigate("/tourpackage");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEditing) {
      editTourPackageMutation.mutate(formData);
    } else {
      addTourPackageMutation.mutate(formData);
    }
  };

  const handleTourSelection = (tourId: string) => {
    const selectedTour = tours.find(tour => tour.id === tourId);
    if (selectedTour) {
      const newSelectedTours = [...formData.selected_tour, selectedTour];
      const newSubTotalChild = newSelectedTours.reduce((sum, tour) => sum + tour.tour_child_price, 0);
      const newSubTotalAdult = newSelectedTours.reduce((sum, tour) => sum + tour.tour_adult_price, 0);
      
      // Calculate parking subtotals for each vehicle type
      const newSubTotalParking = {
        mpv: newSelectedTours.reduce((sum, tour) => sum + (tour.mpv_parking_price || 0), 0),
        elf_short: newSelectedTours.reduce((sum, tour) => sum + (tour.elf_short_parking_price || 0), 0),
        hiace: newSelectedTours.reduce((sum, tour) => sum + (tour.hiace_parking_price || 0), 0),
        elf_giga: newSelectedTours.reduce((sum, tour) => sum + (tour.elf_giga_parking_price || 0), 0),
        bus: newSelectedTours.reduce((sum, tour) => sum + (tour.bus_parking_price || 0), 0),
      };
      
      setFormData({
        ...formData,
        tours_id: tourId,
        selected_tour: newSelectedTours,
        tour_package_name: newSelectedTours.map(tour => tour.tour_name).join(" + "),
        sub_total_price_child: newSubTotalChild,
        sub_total_price_adult: newSubTotalAdult,
        sub_total_parking_price: newSubTotalParking,
      });
      setPopoverOpen(false);
    }
  };

  const removeTour = (tourId: string) => {
    const newSelectedTours = formData.selected_tour.filter(tour => tour.id !== tourId);
    const newSubTotalChild = newSelectedTours.reduce((sum, tour) => sum + tour.tour_child_price, 0);
    const newSubTotalAdult = newSelectedTours.reduce((sum, tour) => sum + tour.tour_adult_price, 0);
    const newSubTotalParking = {
      mpv: newSelectedTours.reduce((sum, tour) => sum + (tour.mpv_parking_price || 0), 0),
      elf_short: newSelectedTours.reduce((sum, tour) => sum + (tour.elf_short_parking_price || 0), 0),
      hiace: newSelectedTours.reduce((sum, tour) => sum + (tour.hiace_parking_price || 0), 0),
      elf_giga: newSelectedTours.reduce((sum, tour) => sum + (tour.elf_giga_parking_price || 0), 0),
      bus: newSelectedTours.reduce((sum, tour) => sum + (tour.bus_parking_price || 0), 0),
    };
    
    setFormData({
      ...formData,
      tours_id: newSelectedTours.length > 0 ? newSelectedTours[newSelectedTours.length - 1].id : "",
      selected_tour: newSelectedTours,
      tour_package_name: newSelectedTours.map(tour => tour.tour_name).join(" + "),
      sub_total_price_child: newSubTotalChild,
      sub_total_price_adult: newSubTotalAdult,
      sub_total_parking_price: newSubTotalParking,
    });
  };

  const handleItineraryChange = (itinerary: string) => {
    setFormData({
      ...formData,
      itinerary,
    });
  };

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Filter tours based on search query and already selected tours
  const filteredTours = tours.filter(tour => 
    !formData.selected_tour.some(selected => selected.id === tour.id) && 
    tour.tour_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container py-10">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEditing ? "Edit Tour Package" : "Add Tour Package"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {isEditing
              ? "Make changes to your tour package here."
              : "Create a new tour package here."}
          </p>
        </div>
        <div className="flex gap-4">
          <Button variant="outline" onClick={() => navigate("/tourpackage")}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={formData.selected_tour.length === 0}>
            {isEditing ? "Save changes" : "Create package"}
          </Button>
        </div>
      </div>

      <div className="space-y-8">
        <div>
          <Label>Package Name</Label>
          <Input
            value={formData.tour_package_name}
            disabled
            placeholder="Will be generated from selected tours"
          />
        </div>

        <div>
          <Label>Selected Tours</Label>
          <div className="mt-2 space-y-4">
            <div className="flex gap-4">
              <div className="w-[300px]">
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={popoverOpen}
                      className="w-full justify-between text-xs"
                    >
                      <span className="truncate">+ Add a tour to package</span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput 
                        placeholder="Search tours..." 
                        className="text-xs"
                        value={searchQuery}
                        onValueChange={setSearchQuery}
                      />
                      <CommandList>
                        <CommandEmpty>No tours found</CommandEmpty>
                        <CommandGroup>
                          {filteredTours.length > 0 ? (
                            filteredTours.map((tour) => (
                              <CommandItem
                                key={tour.id}
                                value={tour.tour_name}
                                onSelect={() => handleTourSelection(tour.id)}
                                className="p-2 text-xs"
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    formData.selected_tour.some(selected => selected.id === tour.id) 
                                      ? "opacity-100" 
                                      : "opacity-0"
                                  )}
                                />
                                {tour.tour_name}
                              </CommandItem>
                            ))
                          ) : (
                            <div className="p-2 text-xs text-center">No matching tours found</div>
                          )}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {formData.selected_tour.length > 0 ? (
              <div className="space-y-1">
                {formData.selected_tour.map((tour) => (
                  <div 
                    key={tour.id} 
                    className="flex items-center justify-between rounded-md border border-gray-800 p-2 h-8 hover:bg-transparent"
                  >
                    <span className="text-xs">{tour.tour_name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeTour(tour.id)}
                      className="h-4 w-4 hover:bg-transparent"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-8 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium">No tours selected</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Select tours from the dropdown above to create a package
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Transport Pricing Sections */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium">Transport Pricing</h3>
            
          {/* MPV Transport */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">MPV Transport (1-6 Person)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Transport (Kuta)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.transport_kuta}
                  onChange={(e) =>
                    setFormData({ ...formData, transport_kuta: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Nusa Dua)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.transport_nusadua}
                  onChange={(e) =>
                    setFormData({ ...formData, transport_nusadua: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Ubud)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.transport_ubud}
                  onChange={(e) =>
                    setFormData({ ...formData, transport_ubud: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Other)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.transport_other}
                  onChange={(e) =>
                    setFormData({ ...formData, transport_other: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>
            </div>
          </div>

          {/* Elf Short Transport */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Elf Short Transport (1-12 Person)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Transport (Kuta)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_short_transport_kuta}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_short_transport_kuta: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Nusa Dua)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_short_transport_nusadua}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_short_transport_nusadua: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Ubud)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_short_transport_ubud}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_short_transport_ubud: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Other)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_short_transport_other}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_short_transport_other: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>
            </div>
          </div>

          {/* Hiace Transport */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Hiace Transport (1-15 Person)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Transport (Kuta)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.hiace_transport_kuta}
                  onChange={(e) =>
                    setFormData({ ...formData, hiace_transport_kuta: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Nusa Dua)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.hiace_transport_nusadua}
                  onChange={(e) =>
                    setFormData({ ...formData, hiace_transport_nusadua: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Ubud)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.hiace_transport_ubud}
                  onChange={(e) =>
                    setFormData({ ...formData, hiace_transport_ubud: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Other)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.hiace_transport_other}
                  onChange={(e) =>
                    setFormData({ ...formData, hiace_transport_other: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>
            </div>
          </div>

          {/* Elf Giga Transport */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Elf Giga Transport (1-19 Person)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Transport (Kuta)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_giga_transport_kuta}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_giga_transport_kuta: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Nusa Dua)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_giga_transport_nusadua}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_giga_transport_nusadua: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Ubud)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_giga_transport_ubud}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_giga_transport_ubud: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Other)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.elf_giga_transport_other}
                  onChange={(e) =>
                    setFormData({ ...formData, elf_giga_transport_other: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>
            </div>
          </div>

          {/* Bus Transport */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Bus Transport (1-30 Person)</h4>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Transport (Kuta)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.bus_transport_kuta}
                  onChange={(e) =>
                    setFormData({ ...formData, bus_transport_kuta: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Nusa Dua)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.bus_transport_nusadua}
                  onChange={(e) =>
                    setFormData({ ...formData, bus_transport_nusadua: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Ubud)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.bus_transport_ubud}
                  onChange={(e) =>
                    setFormData({ ...formData, bus_transport_ubud: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Transport (Other)</Label>
                <Input
                  type="number"
                  min="0"
                  value={formData.bus_transport_other}
                  onChange={(e) =>
                    setFormData({ ...formData, bus_transport_other: Math.max(0, parseInt(e.target.value) || 0) })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-lg font-medium">Sub Total Summary</h3>
          
          {/* Sub Total Entrance Fee */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Sub Total Entrance Fee</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sub Total (Adult)</Label>
                <Input
                  type="number"
                  value={formData.sub_total_price_adult}
                  disabled
                />
              </div>

              <div className="space-y-2">
                <Label>Sub Total (Child)</Label>
                <Input
                  type="number"
                  value={formData.sub_total_price_child}
                  disabled
                />
              </div>
            </div>
          </div>
          
          {/* Sub Total Parking */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">Sub Total Parking</h4>
            <div className="grid grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>MPV</Label>
                <Input
                  type="number"
                  value={formData.sub_total_parking_price.mpv}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label>Elf Short</Label>
                <Input
                  type="number"
                  value={formData.sub_total_parking_price.elf_short}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label>Hiace</Label>
                <Input
                  type="number"
                  value={formData.sub_total_parking_price.hiace}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label>Elf Giga</Label>
                <Input
                  type="number"
                  value={formData.sub_total_parking_price.elf_giga}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <Label>Bus</Label>
                <Input
                  type="number"
                  value={formData.sub_total_parking_price.bus}
                  disabled
                />
              </div>
            </div>
          </div>
        </div>

        <div>
          <Label>Itinerary</Label>
          <div className="mt-2">
            <ItineraryForm
              content={formData.itinerary}
              onChange={handleItineraryChange}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
